import React, { useState, useEffect } from 'react';
import { Check, Plus, Trash2, RefreshCw, AlertCircle, ListOrdered } from 'lucide-react';
import { projectAPI, interactiveCardsAPI } from '../services/api';

const PlanUpdateCard = ({ card, projectId, messageId, handledCardIds = [], cardStates = {}, onUpdateComplete, className = '' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Check if card is already handled (persisted state from database)
  useEffect(() => {
    if (card?.id && handledCardIds.includes(card.id)) {
      // Card was previously handled - check specific state from message metadata
      const cardState = cardStates[card.id];
      if (cardState === 'completed') {
        setCompleted(true);
      } else if (cardState === 'dismissed') {
        setDismissed(true);
      } else {
        // Fallback to localStorage (for cards handled before this update)
        const storedState = localStorage.getItem(`plan_update_${card.id}_state`);
        if (storedState === 'completed') {
          setCompleted(true);
        } else if (storedState === 'dismissed') {
          setDismissed(true);
        } else {
          // Default to completed if handled but no specific state
          setCompleted(true);
        }
      }
    }
  }, [card?.id, handledCardIds, cardStates]);

  // The proposal array comes from the backend response
  const proposal = card.proposal || [];

  const handleConfirm = async () => {
    if (!projectId) {
        setError("Missing Project ID");
        return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        changes: proposal,
        objectivesSignature: card.objectives_signature || card.objectivesSignature || null
      };
      await projectAPI.batchUpdateObjectives(projectId, payload);
      setCompleted(true);
      
      // Mark card as handled in database for persistence
      if (messageId && card?.id) {
        try {
          await interactiveCardsAPI.markHandled({ messageId, cardId: card.id, state: 'completed' });
          // Also store in localStorage as backup
          localStorage.setItem(`plan_update_${card.id}_state`, 'completed');
        } catch (err) {
          console.warn("Failed to mark plan update card as handled:", err);
          // Don't fail the whole operation if this fails
        }
      }
      
      if (onUpdateComplete) onUpdateComplete();
    } catch (err) {
      console.error("Failed to update plan:", err);
      if (err?.response?.status === 409) {
        setError('Plan changed in the background. Please review the latest objectives before applying.');
      } else {
      setError(err.message || 'Failed to update plan');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    setDismissed(true);
    
    // Mark card as handled in database for persistence
    if (messageId && card?.id) {
      try {
        await interactiveCardsAPI.markHandled({ messageId, cardId: card.id, state: 'dismissed' });
        // Also store in localStorage as backup
        localStorage.setItem(`plan_update_${card.id}_state`, 'dismissed');
      } catch (err) {
        console.warn("Failed to mark plan update card as dismissed:", err);
        // Don't fail the whole operation if this fails
      }
    }
  };

  if (completed) {
    return (
      <div className={`bg-green-50 dark:bg-green-900/20 p-4 rounded-xl border border-green-200 dark:border-green-800 ${className}`}>
        <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
          <Check size={20} />
          <span className="font-medium">Plan updated successfully!</span>
        </div>
      </div>
    );
  }

  if (dismissed) {
    return (
      <div className={`mt-4 flex justify-center ${className}`}>
        <button 
          onClick={() => setDismissed(false)}
          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1.5 transition-colors py-2"
        >
          <RefreshCw size={12} />
          <span>Plan update dismissed (Undo)</span>
        </button>
      </div>
    );
  }

  if (!proposal || proposal.length === 0) {
      return null;
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl border border-blue-100 dark:border-blue-900/50 shadow-lg shadow-blue-100/50 dark:shadow-none ring-1 ring-blue-50 dark:ring-blue-900/20 overflow-hidden ${className}`}>
      {/* Header row with icon pattern (matching other cards) */}
      <div className="flex items-start gap-6 p-4 pb-4">
        {/* Icon column - consistent width like other cards */}
        <div className="flex-shrink-0">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/50 dark:to-blue-800/30 text-blue-600 dark:text-blue-400 shadow-sm ring-1 ring-blue-200/50 dark:ring-blue-700/30">
            <RefreshCw size={22} strokeWidth={2.5} />
          </div>
        </div>
        
        {/* Title/subtitle */}
        <div className="flex-1 min-w-0 pt-1">
          <h3 className="text-base font-bold text-gray-900 dark:text-gray-50 leading-tight">
            {card.title || 'Review Changes'}
          </h3>
          {card.subtitle && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{card.subtitle}</p>
          )}
        </div>
      </div>
      
      {/* Proposal items */}
      <div className="px-4 pb-4 space-y-2">
        {proposal.map((change, idx) => (
          <div key={idx} className="flex items-start gap-3 text-sm p-3 bg-blue-50/50 dark:bg-blue-900/10 rounded-xl">
            <div className="mt-0.5 flex-shrink-0">
              {change.action === 'create' && <Plus size={16} className="text-green-500" />}
              {change.action === 'delete' && <Trash2 size={16} className="text-red-500" />}
              {change.action === 'update' && <RefreshCw size={16} className="text-blue-500" />}
              {change.action === 'reorder' && <ListOrdered size={16} className="text-orange-500" />}
            </div>
            <div className="flex-1 min-w-0">
              {change.action === 'create' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-green-700 dark:text-green-400">Add:</span> {change.name}
                  {change.description && <div className="text-xs text-gray-500 mt-0.5">{change.description}</div>}
                </div>
              )}
              {change.action === 'delete' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-red-700 dark:text-red-400">Remove:</span> {change.name || `Objective`}
                </div>
              )}
              {change.action === 'update' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-blue-700 dark:text-blue-400">Update:</span> {change.name || 'Objective'}
                  {change.status && <span className="text-xs ml-2 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/40 rounded text-blue-700 dark:text-blue-300 uppercase tracking-wider font-medium">{change.status}</span>}
                </div>
              )}
              {change.action === 'reorder' && (
                <div className="text-gray-700 dark:text-gray-300">
                  <span className="font-medium text-orange-700 dark:text-orange-400">Reorder List</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="px-4 pb-3 text-sm text-red-600 flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Action buttons - primary left, secondary right */}
      <div className="px-4 pb-4 pt-1 flex gap-3">
        <button
          onClick={handleConfirm}
          disabled={loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50 transition-colors shadow-md shadow-blue-600/20 hover:shadow-lg hover:shadow-blue-600/25 active:scale-[0.98]"
        >
          {loading ? <RefreshCw size={16} className="animate-spin" /> : <Check size={16} />}
          Apply Changes
        </button>
        <button
          onClick={handleDismiss}
          className="px-4 py-3 rounded-xl font-medium text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
        >
          Skip
        </button>
      </div>
    </div>
  );
};

export default PlanUpdateCard;

